import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixPaymentsPayinsConfigurationsV1PayinsConfiguration from './payments-payins-configurations-v1-payins-configuration-payins-configurations.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** PayinsConfiguration */
export interface PayinsConfiguration {
  /**
   * id
   * @format GUID
   * @readonly
   * @immutable
   */
  _id?: string | null;
  /**
   * app_id
   * @format GUID
   * @immutable
   */
  appId?: string | null;
  /**
   * rules
   * @maxSize 1000
   */
  rules?: PayinsRule[];
  /**
   * revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Date when PayinsConfiguration was created
   * @readonly
   * @immutable
   */
  _createdDate?: Date | null;
  /**
   * Date when PayinsConfiguration was updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /** Tags */
  tags?: Tags;
}

export interface PayinsRule {
  /**
   * payment_method_type_id
   * @format GUID
   */
  paymentMethodTypeId?: string;
  /**
   * account_connection_id
   * @format GUID
   */
  accountConnectionId?: string;
  /** merchant_selection */
  merchantSelection?: MerchantSelection;
}

export interface MerchantSelection {
  /** payins_enabled */
  payinsEnabled?: boolean;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface CreatePayinsConfigurationRequest {
  /** PayinsConfiguration to be created. */
  payinsConfiguration: PayinsConfiguration;
}

export interface CreatePayinsConfigurationResponse {
  /** The created PayinsConfiguration. */
  payinsConfiguration?: PayinsConfiguration;
}

export interface GetPayinsConfigurationRequest {
  /**
   * ID of the PayinsConfiguration to retrieve.
   * @format GUID
   */
  payinsConfigurationId: string;
}

export interface GetPayinsConfigurationResponse {
  /** The requested PayinsConfiguration. */
  payinsConfiguration?: PayinsConfiguration;
}

export interface UpdatePayinsConfigurationRequest {
  /** PayinsConfiguration to be updated, may be partial. */
  payinsConfiguration: PayinsConfiguration;
}

export interface UpdatePayinsConfigurationResponse {
  /** Updated PayinsConfiguration. */
  payinsConfiguration?: PayinsConfiguration;
}

export interface QueryPayinsConfigurationsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryPayinsConfigurationsResponse {
  /** List of PayinsConfigurations. */
  payinsConfigurations?: PayinsConfiguration[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkUpdatePayinsConfigurationTagsRequest {
  /**
   * List of PayinsConfigurations that their tags will update.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  ids: string[];
  /** List of Tags to assign */
  assignTags: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}

export interface BulkUpdatePayinsConfigurationTagsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdatePayinsConfigurationTagsResult[];
  /** Metadata regarding the bulk update operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdatePayinsConfigurationTagsResult {
  /** Metadata regarding the specific single update operation */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdatePayinsConfigurationTagsByFilterRequest {
  /** Filter */
  filter: Record<string, any> | null;
  /** List of Tags to assign */
  assignTags: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}

export interface BulkUpdatePayinsConfigurationTagsByFilterResponse {
  /**
   * Job ID
   * @format GUID
   */
  jobId?: string;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /**
   * Unique event ID.
   * Allows clients to ignore duplicate webhooks.
   */
  _id?: string;
  /**
   * Assumes actions are also always typed to an entity_type
   * Example: wix.stores.catalog.product, wix.bookings.session, wix.payments.transaction
   */
  entityFqdn?: string;
  /**
   * This is top level to ease client code dispatching of messages (switch on entity_fqdn+slug)
   * This is although the created/updated/deleted notion is duplication of the oneof types
   * Example: created/updated/deleted/started/completed/email_opened
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example: 2020-04-26T13:57:50.699Z */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number defining the order of updates to the underlying entity.
   * For example, given that some entity was updated at 16:00 and than again at 16:01,
   * it is guaranteed that the sequence number of the second update is strictly higher than the first.
   * As the consumer, you can use this value to ensure that you handle messages in the correct order.
   * To do so, you will need to persist this number on your end, and compare the sequence number from the
   * message against the one you have stored. Given that the stored number is higher, you should ignore the message.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

/**
 * Creates a PayinsConfiguration.
 * @param payinsConfiguration - PayinsConfiguration to be created.
 * @internal
 * @documentationMaturity preview
 * @requiredField payinsConfiguration
 * @permissionId PAYMENTS.PAYINS_CONFIGURATION_CREATE
 * @returns The created PayinsConfiguration.
 * @fqn wix.payments.payins_configurations.v1.PayinsConfigurationService.CreatePayinsConfiguration
 */
export async function createPayinsConfiguration(
  payinsConfiguration: PayinsConfiguration
): Promise<
  NonNullablePaths<
    PayinsConfiguration,
    | `rules`
    | `rules.${number}.paymentMethodTypeId`
    | `rules.${number}.accountConnectionId`
    | `rules.${number}.merchantSelection.payinsEnabled`
    | `tags.privateTags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    payinsConfiguration: payinsConfiguration,
  });

  const reqOpts =
    ambassadorWixPaymentsPayinsConfigurationsV1PayinsConfiguration.createPayinsConfiguration(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.payinsConfiguration!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { payinsConfiguration: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['payinsConfiguration']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a PayinsConfiguration.
 * @param payinsConfigurationId - ID of the PayinsConfiguration to retrieve.
 * @internal
 * @documentationMaturity preview
 * @requiredField payinsConfigurationId
 * @permissionId PAYMENTS.PAYINS_CONFIGURATION_READ
 * @returns The requested PayinsConfiguration.
 * @fqn wix.payments.payins_configurations.v1.PayinsConfigurationService.GetPayinsConfiguration
 */
export async function getPayinsConfiguration(
  payinsConfigurationId: string
): Promise<
  NonNullablePaths<
    PayinsConfiguration,
    | `rules`
    | `rules.${number}.paymentMethodTypeId`
    | `rules.${number}.accountConnectionId`
    | `rules.${number}.merchantSelection.payinsEnabled`
    | `tags.privateTags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    payinsConfigurationId: payinsConfigurationId,
  });

  const reqOpts =
    ambassadorWixPaymentsPayinsConfigurationsV1PayinsConfiguration.getPayinsConfiguration(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.payinsConfiguration!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { payinsConfigurationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['payinsConfigurationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a PayinsConfiguration.
 *
 * Each time the PayinsConfiguration is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the PayinsConfiguration.
 * This ensures you're working with the latest PayinsConfiguration
 * and prevents unintended overwrites.
 * @param _id - id
 * @internal
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField payinsConfiguration
 * @requiredField payinsConfiguration.revision
 * @permissionId PAYMENTS.PAYINS_CONFIGURATION_UPDATE
 * @returns Updated PayinsConfiguration.
 * @fqn wix.payments.payins_configurations.v1.PayinsConfigurationService.UpdatePayinsConfiguration
 */
export async function updatePayinsConfiguration(
  _id: string,
  payinsConfiguration: NonNullablePaths<
    UpdatePayinsConfiguration,
    `revision`,
    2
  >
): Promise<
  NonNullablePaths<
    PayinsConfiguration,
    | `rules`
    | `rules.${number}.paymentMethodTypeId`
    | `rules.${number}.accountConnectionId`
    | `rules.${number}.merchantSelection.payinsEnabled`
    | `tags.privateTags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    payinsConfiguration: { ...payinsConfiguration, id: _id },
  });

  const reqOpts =
    ambassadorWixPaymentsPayinsConfigurationsV1PayinsConfiguration.updatePayinsConfiguration(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.payinsConfiguration!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { payinsConfiguration: '$[1]' },
        explicitPathsToArguments: { 'payinsConfiguration.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'payinsConfiguration']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdatePayinsConfiguration {
  /**
   * id
   * @format GUID
   * @readonly
   * @immutable
   */
  _id?: string | null;
  /**
   * app_id
   * @format GUID
   * @immutable
   */
  appId?: string | null;
  /**
   * rules
   * @maxSize 1000
   */
  rules?: PayinsRule[];
  /**
   * revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Date when PayinsConfiguration was created
   * @readonly
   * @immutable
   */
  _createdDate?: Date | null;
  /**
   * Date when PayinsConfiguration was updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /** Tags */
  tags?: Tags;
}

/**
 * Retrieves a list of PayinsConfigurations, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 PayinsConfigurations can be returned per request.
 *
 * To learn how to query PayinsConfigurations, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 * @internal
 * @documentationMaturity preview
 * @permissionId PAYMENTS.PAYINS_CONFIGURATION_READ
 * @fqn wix.payments.payins_configurations.v1.PayinsConfigurationService.QueryPayinsConfigurations
 */
export function queryPayinsConfigurations(): PayinsConfigurationsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    PayinsConfiguration,
    'CURSOR',
    QueryPayinsConfigurationsRequest,
    QueryPayinsConfigurationsResponse
  >({
    func: async (payload: QueryPayinsConfigurationsRequest) => {
      const reqOpts =
        ambassadorWixPaymentsPayinsConfigurationsV1PayinsConfiguration.queryPayinsConfigurations(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryPayinsConfigurationsRequest['query']) => {
      const args = [query, {}] as [
        QueryPayinsConfigurationsRequest['query'],
        {}
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryPayinsConfigurationsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.payinsConfigurations,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface PayinsConfigurationsQueryResult extends QueryCursorResult {
  items: PayinsConfiguration[];
  query: PayinsConfigurationsQueryBuilder;
  next: () => Promise<PayinsConfigurationsQueryResult>;
  prev: () => Promise<PayinsConfigurationsQueryResult>;
}

export interface PayinsConfigurationsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantSelection.payinsEnabled',
    value: any
  ) => PayinsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantSelection.payinsEnabled',
    value: any
  ) => PayinsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId',
    value: any
  ) => PayinsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId',
    value: any
  ) => PayinsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId',
    value: any
  ) => PayinsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId',
    value: any
  ) => PayinsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId',
    value: string
  ) => PayinsConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantSelection.payinsEnabled',
    value: any[]
  ) => PayinsConfigurationsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantSelection.payinsEnabled',
    value: any
  ) => PayinsConfigurationsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantSelection.payinsEnabled',
    value: boolean
  ) => PayinsConfigurationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantSelection.payinsEnabled'
    >
  ) => PayinsConfigurationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantSelection.payinsEnabled'
    >
  ) => PayinsConfigurationsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => PayinsConfigurationsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => PayinsConfigurationsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<PayinsConfigurationsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.payments.payins_configurations.v1.PayinsConfigurationService.QueryPayinsConfigurations
 * @requiredField query
 */
export async function typedQueryPayinsConfigurations(
  query: PayinsConfigurationQuery
): Promise<
  NonNullablePaths<QueryPayinsConfigurationsResponse, `payinsConfigurations`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixPaymentsPayinsConfigurationsV1PayinsConfiguration.queryPayinsConfigurations(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PayinsConfigurationQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        'rules.accountConnectionId',
        'rules.merchantSelection.payinsEnabled',
        'rules.paymentMethodTypeId'
      ];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    },
    {
      fields: ['_id', 'appId'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  PayinsConfiguration,
  PayinsConfigurationQuerySpec
>;
export type PayinsConfigurationQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      PayinsConfiguration,
      PayinsConfigurationQuerySpec,
      PayinsConfigurationQuery
    >(),
  },
};

/**
 * Synchronously update tags on multiple PayinsConfigurations, by list of PayinsConfigurations ids
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 * @param ids - List of PayinsConfigurations that their tags will update.
 * @internal
 * @documentationMaturity preview
 * @requiredField ids
 * @requiredField options.assignTags
 * @permissionId PAYMENTS.PAYINS_CONFIGURATION_UPDATE_TAGS
 * @fqn wix.payments.payins_configurations.v1.PayinsConfigurationService.BulkUpdatePayinsConfigurationTags
 */
export async function bulkUpdatePayinsConfigurationTags(
  ids: string[],
  options?: NonNullablePaths<
    BulkUpdatePayinsConfigurationTagsOptions,
    `assignTags`,
    2
  >
): Promise<
  NonNullablePaths<
    BulkUpdatePayinsConfigurationTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    ids: ids,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixPaymentsPayinsConfigurationsV1PayinsConfiguration.bulkUpdatePayinsConfigurationTags(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          ids: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['ids', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdatePayinsConfigurationTagsOptions {
  /** List of Tags to assign */
  assignTags: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}

/**
 * Asynchronously update tags on multiple PayinsConfigurations, by provided filter
 * An empty filter will update all PayinsConfigurations
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 * @param filter - Filter
 * @internal
 * @documentationMaturity preview
 * @requiredField filter
 * @requiredField options.assignTags
 * @permissionId PAYMENTS.PAYINS_CONFIGURATION_UPDATE_TAGS
 * @fqn wix.payments.payins_configurations.v1.PayinsConfigurationService.BulkUpdatePayinsConfigurationTagsByFilter
 */
export async function bulkUpdatePayinsConfigurationTagsByFilter(
  filter: Record<string, any>,
  options?: NonNullablePaths<
    BulkUpdatePayinsConfigurationTagsByFilterOptions,
    `assignTags`,
    2
  >
): Promise<
  NonNullablePaths<
    BulkUpdatePayinsConfigurationTagsByFilterResponse,
    `jobId`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixPaymentsPayinsConfigurationsV1PayinsConfiguration.bulkUpdatePayinsConfigurationTagsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdatePayinsConfigurationTagsByFilterOptions {
  /** List of Tags to assign */
  assignTags: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}
